import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/elements/button';
import { Back, User, Mail, Phone, Map, Check, } from '../../assets/icons';
import '../../assets/css/admin/doctor-detail.css';
import { doctorApi } from '../../network/apis/doctors';
import { casesApi } from '../../network/apis/cases';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const AdminDoctorDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [doctor, setDoctor] = useState(null);
    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    const fetchDoctorCases = useCallback(async () => {
        try {
            const response = await casesApi.getCases({ params: { doctor_id: id } });
            if (response?.data?.data) {
                setCases(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch doctor cases:', error);
        }
    }, [id]);

    const fetchDoctorDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await doctorApi.getDoctorById(id);
            if (response?.data) {
                setDoctor(response.data);
                // Also fetch cases for this doctor
                fetchDoctorCases();
            }
        } catch (error) {
            console.error('Failed to fetch doctor:', error);
            toast.error('Failed to load doctor details');
        } finally {
            setLoading(false);
        }
    }, [id, fetchDoctorCases]);

    useEffect(() => {
        fetchDoctorDetails();
    }, [fetchDoctorDetails]);
    
    const handleVerifyDoctor = async () => {
        try {
            setVerifying(true);
            await doctorApi.verifyDoctor(id);
            toast.success('Doctor verified successfully');
            fetchDoctorDetails();
        } catch (error) {
            console.error('Failed to verify doctor:', error);
            toast.error(error.response?.data?.message || 'Failed to verify doctor');
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-doctor-detail">
                <div className="loading-state">
                    <LoadingSpinner />
                    <p>Loading doctor details...</p>
                </div>
            </div>
        );
    }

    if (!doctor) {
        return (
            <div className="admin-doctor-detail">
                <div className="error-state">
                    <p>Doctor not found</p>
                    <Button text="Go Back" onClick={() => navigate('/admin/doctors')} />
                </div>
            </div>
        );
    }

    return (
        <div className="admin-doctor-detail">
            <div className="page-header">
                <div className="header-left">
                    <button className="back-btn" onClick={() => navigate('/admin/doctors')}>
                        <img src={Back} alt="Back" />
                    </button>
                    <div className="header-content">
                        <h1>Dr. {doctor.user_details.first_name} {doctor.user_details.last_name}</h1>
                        <p>{doctor.specialization}</p>
                    </div>
                </div>
                <div className="header-actions">
                    {!doctor.is_verified && (
                        <Button
                            text={verifying ? 'Verifying...' : 'Verify Doctor'}
                            variant="success"
                            icon={<img src={Check} alt="" />}
                            onClick={handleVerifyDoctor}
                            disabled={verifying}
                        />
                    )}
                </div>
            </div>

            {/* Doctor Status Banner */}
            {!doctor.is_verified && (
                <div className="verification-banner warning">
                    <span>⚠️ This doctor is pending verification</span>
                </div>
            )}

            <div className="doctor-content">
                {/* Profile Card */}
                <div className="profile-card">
                    <div className="profile-header">
                        <div className="profile-avatar">
                            {doctor.profile_picture ? (
                                <img src={doctor.profile_picture} alt={doctor.user_details.first_name} />
                            ) : (
                                <img src={User} alt="" className="placeholder" />
                            )}
                        </div>
                        <div className="profile-info">
                            <h2>Dr. {doctor.user_details.first_name} {doctor.user_details.last_name}</h2>
                            <span className="specialization">{doctor.specialization}</span>
                            <div className="status-badges">
                                <span className={`badge ${doctor.is_verified ? 'verified' : 'pending'}`}>
                                    {doctor.is_verified ? '✓ Verified' : 'Pending Verification'}
                                </span>
                                {doctor.years_of_experience && (
                                    <span className="badge experience">{doctor.years_of_experience} years exp.</span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="contact-info">
                        <div className="contact-item">
                            <img src={Mail} alt="" />
                            <span>{doctor.email || doctor.user_details?.email || 'Not provided'}</span>
                        </div>
                        <div className="contact-item">
                            <img src={Phone} alt="" />
                            <span>{doctor.phone_number || doctor.user_details?.phone || 'Not provided'}</span>
                        </div>
                        <div className="contact-item">
                            <img src={Map} alt="" />
                            <span>{doctor.state || doctor.city || 'Location not specified'}</span>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="tabs-section">
                    <div className="tabs-header">
                        <button 
                            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                            onClick={() => setActiveTab('overview')}
                        >
                            Overview
                        </button>
                        <button 
                            className={`tab ${activeTab === 'cases' ? 'active' : ''}`}
                            onClick={() => setActiveTab('cases')}
                        >
                            Cases ({cases.length})
                        </button>
                        <button 
                            className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
                            onClick={() => setActiveTab('documents')}
                        >
                            Documents
                        </button>
                    </div>

                    <div className="tab-content">
                        {activeTab === 'overview' && (
                            <div className="overview-tab">
                                <div className="info-section">
                                    <h3>Professional Information</h3>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <label>License Number</label>
                                            <span>{doctor.license_number || 'Not provided'}</span>
                                        </div>
                                        <div className="info-item">
                                            <label>Specialization</label>
                                            <span>{doctor.specialization || 'Not specified'}</span>
                                        </div>
                                        <div className="info-item">
                                            <label>Years of Experience</label>
                                            <span>{doctor.experience || 'Not specified'}</span>
                                        </div>
                                        <div className="info-item">
                                            <label>Consultation Fee</label>
                                            <span>{doctor.consultation_fee ? `₦${doctor.consultation_fee}` : 'Not set'}</span>
                                        </div>
                                    </div>
                                </div>

                                {doctor.bio && (
                                    <div className="info-section">
                                        <h3>About</h3>
                                        <p className="bio-text">{doctor.bio}</p>
                                    </div>
                                )}

                                {doctor.qualifications && doctor.qualifications.length > 0 && (
                                    <div className="info-section">
                                        <h3>Qualifications</h3>
                                        <div className="qualifications-list">
                                            {doctor.qualifications.map((qual, index) => (
                                                <div key={index} className="qualification-item">
                                                    <strong>{qual.degree || qual.name}</strong>
                                                    <span>{qual.institution}</span>
                                                    <span className="year">{qual.year}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="info-section">
                                    <h3>Statistics</h3>
                                    <div className="stats-grid">
                                        <div className="stat-item">
                                            <div className="stat-value">{cases.length}</div>
                                            <div className="stat-label">Total Cases</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-value">
                                                {cases.filter(c => c.status === 'RESOLVED').length}
                                            </div>
                                            <div className="stat-label">Resolved</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-value">
                                                {cases.filter(c => ['IN_PROGRESS', 'ASSIGNED'].includes(c.status)).length}
                                            </div>
                                            <div className="stat-label">Active</div>
                                        </div>
                                        <div className="stat-item">
                                            <div className="stat-value">{doctor.rating || '—'}</div>
                                            <div className="stat-label">Rating</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'cases' && (
                            <div className="cases-tab">
                                {cases.length > 0 ? (
                                    <table className="cases-table">
                                        <thead>
                                            <tr>
                                                <th>Case ID</th>
                                                <th>Title</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {cases.map(caseItem => (
                                                <tr key={caseItem.id}>
                                                    <td>{caseItem.case_code || `CS${caseItem.id}`}</td>
                                                    <td>{caseItem.title}</td>
                                                    <td>
                                                        <span className={`status ${caseItem.status?.toLowerCase()}`}>
                                                            {caseItem.status?.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td>{new Date(caseItem.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        <Button
                                                            text="View"
                                                            variant="outline"
                                                            small
                                                            onClick={() => navigate(`/admin/cases/${caseItem.id}`)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div className="empty-state">
                                        <p>No cases assigned to this doctor yet</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'documents' && (
                            <div className="documents-tab">
                                <div className="documents-grid">
                                    {doctor.license_document && (
                                        <div className="document-card">
                                            <div className="document-icon">📄</div>
                                            <div className="document-info">
                                                <strong>License Document</strong>
                                                <a href={doctor.license_document} target="_blank" rel="noopener noreferrer">
                                                    View Document
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    {doctor.cv && (
                                        <div className="document-card">
                                            <div className="document-icon">📄</div>
                                            <div className="document-info">
                                                <strong>CV/Resume</strong>
                                                <a href={doctor.cv} target="_blank" rel="noopener noreferrer">
                                                    View Document
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    {(!doctor.license_document && !doctor.cv) && (
                                        <div className="empty-state">
                                            <p>No documents uploaded</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDoctorDetail;
